import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  GetEmailBuilderMedia,
  GetEmailBuilderStockMedia,
  PostEmailBuilderMedia,
} from "api/rpc/2024-04/facilityAdmin/marketing/emailBuilder";
import type { IEmailBuilderMediaItem } from "api/rpc/2024-04/facilityAdmin/marketing/emailBuilderTypes";
import { StatusCode } from "api/protocols";
import Portal from "elements/Portal";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";
import { BuilderMediaGalleryPanel } from "pages/secure/facility/builders/shared/components/mediaGallery/BuilderMediaGalleryPanel";
import { BuilderSettingsSheet } from "pages/secure/facility/builders/shared/components/settingsSheet/BuilderSettingsSheet";
import { WebsiteBuilderFileDropZone } from "pages/secure/facility/websiteBuilder/shared/components/fileDropZone/WebsiteBuilderFileDropZone";
import type { TWebsiteBuilderFileType } from "pages/secure/facility/websiteBuilder/shared/components/fileDropZone/WebsiteBuilderFileDropZone";
import {
  EMAIL_BUILDER_MEDIA_ALLOWED_FILE_TYPES,
  createEmailBuilderFacilityLogoMediaItem,
  filterActiveEmailBuilderMedia,
  filterActiveEmailBuilderStockMedia,
  filterSocialIconStockMedia,
  isEmailBuilderFacilityLogoMediaId,
  registerEmailBuilderLibraryMediaItems,
  upsertEmailBuilderMediaItem,
} from "../../../operations/emailBuilderMediaAdapter";
import { resolveImageBlockAlt } from "../../../operations/imageBlockMediaChange";

export type EmailBuilderMediaGalleryModalVariant = "default" | "socialIcon";

export type EmailBuilderMediaGalleryModalProps = {
  mode: "add" | "replace";
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (result: { media: IEmailBuilderMediaItem; alt?: string }) => void;
  initialAlt?: string;
  initialMediaId?: number | null;
  includeFacilityLogo?: boolean;
  linkedImageHint?: boolean;
  variant?: EmailBuilderMediaGalleryModalVariant;
};

function sheetTitle(
  variant: EmailBuilderMediaGalleryModalVariant,
  mode: EmailBuilderMediaGalleryModalProps["mode"],
  t: (key: string) => string,
): string {
  if (variant === "socialIcon") {
    return t("Change icon");
  }

  return mode === "replace" ? t("Replace image") : t("Choose an image");
}

function sheetSupportText(
  variant: EmailBuilderMediaGalleryModalVariant,
  mode: EmailBuilderMediaGalleryModalProps["mode"],
  t: (key: string) => string,
): string | undefined {
  if (variant === "socialIcon") {
    return undefined;
  }

  return mode === "replace"
    ? t("Select an image to replace the current one.")
    : t("Select an image to add one.");
}

function sheetOkText(
  variant: EmailBuilderMediaGalleryModalVariant,
  mode: EmailBuilderMediaGalleryModalProps["mode"],
  t: (key: string) => string,
): string {
  if (variant === "socialIcon") {
    return t("Use icon");
  }

  return mode === "replace" ? t("Replace image") : t("Add image");
}

export function EmailBuilderMediaGalleryModal(props: EmailBuilderMediaGalleryModalProps) {
  const {
    mode,
    isOpen,
    onClose,
    onConfirm,
    initialAlt = "",
    initialMediaId,
    includeFacilityLogo = false,
    linkedImageHint = false,
    variant = "default",
  } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const facility = useAppSelector(store => store.facilityStore.facility);
  const [facilityMedia, setFacilityMedia] = useState<IEmailBuilderMediaItem[]>([]);
  const [stockMedia, setStockMedia] = useState<IEmailBuilderMediaItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedMediaId, setSelectedMediaId] = useState<number | null>(null);
  const [alt, setAlt] = useState(initialAlt);
  const isSocialIconVariant = variant === "socialIcon";
  const showAltField = !isSocialIconVariant;
  const showLinkedImageHint =
    showAltField && linkedImageHint && alt.trim().length === 0;

  const loadMedia = useCallback(async () => {
    setLoading(true);
    try {
      const [facilityRes, stockRes] = await Promise.all([
        GetEmailBuilderMedia(false),
        GetEmailBuilderStockMedia(false),
      ]);

      if (facilityRes.status !== StatusCode.OK || !Array.isArray(facilityRes.data)) {
        dispatch(
          showError(
            typeof facilityRes.message === "string"
              ? facilityRes.message
              : t("Failed to load media"),
          ),
        );
        return;
      }

      if (stockRes.status !== StatusCode.OK || !Array.isArray(stockRes.data)) {
        dispatch(
          showError(
            typeof stockRes.message === "string"
              ? stockRes.message
              : t("Failed to load stock media"),
          ),
        );
        setFacilityMedia(facilityRes.data);
        registerEmailBuilderLibraryMediaItems(facilityRes.data);
        return;
      }

      setFacilityMedia(facilityRes.data);
      setStockMedia(stockRes.data);
      registerEmailBuilderLibraryMediaItems([...facilityRes.data, ...stockRes.data]);
    } finally {
      setLoading(false);
    }
  }, [dispatch, t]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    void loadMedia();
  }, [isOpen, loadMedia]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setAlt(initialAlt);
  }, [initialAlt, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setSelectedMediaId(initialMediaId ?? null);
  }, [initialMediaId, isOpen]);

  async function handleUpload(file: File) {
    const res = await PostEmailBuilderMedia({ file }, true);
    const media = res.data?.data;
    if (res.status !== StatusCode.OK || media == null) {
      dispatch(
        showError(
          typeof res.message === "string"
            ? res.message
            : t("Failed to upload media item"),
        ),
      );
      return;
    }

    registerEmailBuilderLibraryMediaItems([media]);
    setFacilityMedia(current => upsertEmailBuilderMediaItem(current, media));
    setSelectedMediaId(media.id);
    dispatch(showSuccess(t("Successfully uploaded media item")));
  }

  const facilityLogoMediaItem =
    showAltField && includeFacilityLogo
      ? createEmailBuilderFacilityLogoMediaItem(facility ?? null)
      : null;
  const myMediaItems =
    facilityLogoMediaItem !== null
      ? [facilityLogoMediaItem, ...filterActiveEmailBuilderMedia(facilityMedia)]
      : filterActiveEmailBuilderMedia(facilityMedia);
  const stockMediaItems = isSocialIconVariant
    ? filterSocialIconStockMedia(stockMedia)
    : filterActiveEmailBuilderStockMedia(stockMedia);
  const selectedItem =
    selectedMediaId == null
      ? null
      : myMediaItems.find(item => item.id === selectedMediaId) ??
        stockMediaItems.find(item => item.id === selectedMediaId) ??
        null;

  const handleConfirm = useCallback(() => {
    if (selectedItem == null) {
      return;
    }

    if (showAltField) {
      onConfirm({ media: selectedItem, alt });
    } else {
      onConfirm({ media: selectedItem });
    }

    onClose();
  }, [alt, onClose, onConfirm, selectedItem, showAltField]);

  return (
    <Portal isMounted={isOpen} parentId="portal-root">
      <BuilderSettingsSheet
        open={isOpen}
        title={sheetTitle(variant, mode, t)}
        supportText={sheetSupportText(variant, mode, t)}
        size="medium"
        okText={sheetOkText(variant, mode, t)}
        cancelText={t("Cancel")}
        okDisabled={selectedItem == null}
        onOk={handleConfirm}
        onCancel={onClose}
      >
        <BuilderMediaGalleryPanel
          myMediaItems={myMediaItems}
          stockMediaItems={stockMediaItems}
          selectedMediaId={selectedMediaId}
          onSelectMedia={item => {
            setSelectedMediaId(item.id);
            if (isEmailBuilderFacilityLogoMediaId(item.id)) {
              setAlt(
                resolveImageBlockAlt(
                  { kind: "facility", assetKey: "logo" },
                  alt,
                ),
              );
            }
          }}
          uploadSlot={
            <WebsiteBuilderFileDropZone
              theme="dark"
              condensed
              allowedFileTypes={
                [...EMAIL_BUILDER_MEDIA_ALLOWED_FILE_TYPES] as TWebsiteBuilderFileType[]
              }
              maxImageMegabytes={10}
              saveFile={file => {
                void handleUpload(file);
              }}
            />
          }
          showAltField={showAltField}
          alt={alt}
          onAltChange={setAlt}
          altHint={
            showLinkedImageHint ? (
              <p className="text-sm text-regular text-gray-600" role="status">
                {t(
                  "Linked images need alt text describing the link destination.",
                )}
              </p>
            ) : null
          }
          loading={loading}
        />
      </BuilderSettingsSheet>
    </Portal>
  );
}
